import { useState, useMemo, useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Agent } from "@/types/agent/agent";
import { useTestCategories, useRunElevenLabsTest, useCustomTests } from "@/hooks/agent/useBrandIntegrity";
import { useEvaluationSuggestions } from "@/hooks/agent/useEvaluationSuggestions";
import { TestCategoryCard } from "./TestCategoryCard";
import { CreateCustomTestModal } from "./CreateCustomTestModal";

interface RunTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  agents: Agent[];
  onTestComplete: () => void;
  onTestStarted?: (testRunId: string, agentName: string, totalTests: number) => void;
  preSelectedAgentId?: string | null;
  isTestRunning?: boolean;
}

export const RunTestModal = ({
  isOpen,
  onClose,
  agents,
  onTestComplete,
  onTestStarted,
  preSelectedAgentId,
  isTestRunning = false,
}: RunTestModalProps) => {
  const [selectedAgent, setSelectedAgent] = useState<string | null>(preSelectedAgentId || agents[0]?.id || null);
  const [selectedCoreCategories, setSelectedCoreCategories] = useState<string[]>([]);
  const [selectedSuggestedIds, setSelectedSuggestedIds] = useState<string[]>([]);
  const [selectedCustomIds, setSelectedCustomIds] = useState<string[]>([]);
  const [showCreateCustomTest, setShowCreateCustomTest] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [coreExpanded, setCoreExpanded] = useState(false);
  const [suggestedExpanded, setSuggestedExpanded] = useState(false);
  const [customExpanded, setCustomExpanded] = useState(false);

  const { data: coreCategories = [], isLoading: isLoadingCategories } = useTestCategories();
  const { data: customTests = [], isLoading: isLoadingCustomTests } = useCustomTests();
  const { mutate: runTest, isPending: isTesting } = useRunElevenLabsTest();

  // Get the selected agent object
  const selectedAgentObj = useMemo(() => {
    return agents.find((a) => a.id === selectedAgent);
  }, [agents, selectedAgent]);

  // Check if agent has ElevenLabs agent ID
  const elevenlabsId = selectedAgentObj?.elevenlabs_id;

  // Get AI-generated suggested tests
  const {
    suggestions: suggestedTests,
    isLoading: isLoadingSuggestions,
    isGenerating,
    regenerate,
  } = useEvaluationSuggestions(selectedAgentObj || null);

  // Track if we've initialized the core categories
  const coreCategoriesInitializedRef = useRef(false);
  const suggestedTestsInitializedRef = useRef<string | null>(null);

  // Create stable key for core categories
  const coreCategoryIds = useMemo(() => coreCategories.map((c) => c.id), [coreCategories]);
  const coreCategoryIdsKey = JSON.stringify(coreCategoryIds);

  // Initialize all core categories as selected when they load (only once)
  useEffect(() => {
    if (coreCategoryIds.length > 0 && !coreCategoriesInitializedRef.current) {
      coreCategoriesInitializedRef.current = true;
      setSelectedCoreCategories(coreCategoryIds);
    }
  }, [coreCategoryIdsKey]);

  // Auto-select suggested tests when suggestions change
  const suggestedTestIds = useMemo(() => suggestedTests.map((t) => t.id), [suggestedTests]);
  const suggestedTestIdsKey = JSON.stringify(suggestedTestIds);

  useEffect(() => {
    if (suggestedTestsInitializedRef.current !== suggestedTestIdsKey) {
      suggestedTestsInitializedRef.current = suggestedTestIdsKey;
      setSelectedSuggestedIds(suggestedTestIds);
    }
  }, [suggestedTestIdsKey, suggestedTestIds]);

  const handleToggleCoreCategory = (categoryId: string) => {
    setSelectedCoreCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleToggleSuggested = (testId: string) => {
    setSelectedSuggestedIds((prev) =>
      prev.includes(testId)
        ? prev.filter((id) => id !== testId)
        : [...prev, testId]
    );
  };

  const handleToggleCustom = (testId: string) => {
    setSelectedCustomIds((prev) =>
      prev.includes(testId)
        ? prev.filter((id) => id !== testId)
        : [...prev, testId]
    );
  };

  const handleSelectAll = () => {
    setSelectedCoreCategories(coreCategories.map((c) => c.id));
    setSelectedSuggestedIds(suggestedTests.map((t) => t.id));
    setSelectedCustomIds(customTests.map((t) => t.id));
  };

  const handleDeselectAll = () => {
    setSelectedCoreCategories([]);
    setSelectedSuggestedIds([]);
    setSelectedCustomIds([]);
  };

  const MAX_TESTS = 30;
  const totalSelectedCount = selectedCoreCategories.length + selectedSuggestedIds.length + selectedCustomIds.length;
  const isOverLimit = totalSelectedCount > MAX_TESTS;

  const handleRunTest = () => {
    if (!selectedAgent || !elevenlabsId) {
      toast.error("Please select a genie with an ElevenLabs agent configured");
      return;
    }
    if (isOverLimit) {
      toast.error(`Maximum ${MAX_TESTS} tests per run. Please deselect some tests.`);
      return;
    }

    if (totalSelectedCount === 0) {
      toast.error("Please select at least one test category");
      return;
    }

    const selectedSuggestedTests = suggestedTests.filter((t) => selectedSuggestedIds.includes(t.id));

    runTest(
      {
        agentId: selectedAgent,
        elevenlabsId,
        categories: selectedCoreCategories,
        suggestedTests: selectedSuggestedTests,
        customTestIds: selectedCustomIds,
      },
      {
        onSuccess: (result) => {
          onTestStarted?.(result.id, selectedAgentObj?.name || "Agent", totalSelectedCount);
          toast.success(`Running ${totalSelectedCount} tests in background`, {
            description: "You can navigate away - check the sidebar for progress",
          });
          handleClose();
        },
        onError: (error) => {
          toast.error("Failed to run brand test");
          console.error("Test error:", error);
        },
      }
    );
  };

  const resetForm = () => {
    setSelectedCoreCategories(coreCategories.map((c) => c.id));
    setSelectedSuggestedIds([]);
    setSelectedCustomIds([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      if (preSelectedAgentId) {
        setSelectedAgent(preSelectedAgentId);
      } else if (!selectedAgent && agents.length > 0) {
        setSelectedAgent(agents[0].id);
      }
    }
  }, [isOpen, preSelectedAgentId, agents]);

  const isLoadingAny = isLoadingCategories || isLoadingSuggestions || isLoadingCustomTests;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          showClose={false}
          className="w-full lg:w-3/4 max-w-full lg:max-w-[1200px] bg-white shadow-2xl overflow-hidden p-0"
        >
          <SheetTitle className="sr-only">Run Evaluation</SheetTitle>
          <SheetDescription className="sr-only">Configure and run evaluation tests for your genie</SheetDescription>
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b p-6 sm:p-8">
              <h2 className="text-xl font-bold">Run Evaluation</h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors"
              >
                &times;
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-6">
              {/* Genie Selection */}
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Select Genie
                </label>
                <select
                  value={selectedAgent || ""}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base"
                  disabled={isTesting}
                >
                  <option value="">-- Select a genie --</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
                {selectedAgentObj && !elevenlabsId && (
                  <p className="text-sm text-red-600 mt-2">
                    This genie doesn't have an ElevenLabs agent configured.
                  </p>
                )}
              </div>

              {/* Summary Card with Gradient Border */}
              {selectedAgent && elevenlabsId && (
                <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                  <div className="rounded-[10px] bg-white p-5">
                    {isLoadingAny ? (
                      <div className="flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
                        <span className="text-sm text-gray-600">
                          {isGenerating ? "Generating suggestions..." : "Loading tests..."}
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className={`font-semibold text-lg ${isOverLimit ? "text-red-600" : "text-gray-900"}`}>
                            {totalSelectedCount} / {MAX_TESTS} tests selected
                          </span>
                          <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="text-sm text-genie-primary hover:underline"
                          >
                            {showAdvanced ? "Hide Details" : "Customize"}
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3 text-sm text-gray-600">
                          <span>{selectedCoreCategories.length} Core</span>
                          {selectedSuggestedIds.length > 0 && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span>{selectedSuggestedIds.length} Recommended</span>
                            </>
                          )}
                          {selectedCustomIds.length > 0 && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span>{selectedCustomIds.length} Custom</span>
                            </>
                          )}
                        </div>
                        {isOverLimit && (
                          <p className="mt-2 text-sm text-red-600">
                            Limit is {MAX_TESTS} tests per run — please deselect {totalSelectedCount - MAX_TESTS} test{totalSelectedCount - MAX_TESTS !== 1 ? "s" : ""}.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Advanced: Customize Tests */}
              {showAdvanced && selectedAgent && elevenlabsId && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Customize Tests</span>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSelectAll}
                        disabled={isTesting}
                        className="text-sm text-genie-primary hover:underline disabled:opacity-50"
                      >
                        Select All
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={handleDeselectAll}
                        disabled={isTesting}
                        className="text-sm text-genie-primary hover:underline disabled:opacity-50"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>

                  {/* Core Tests Section */}
                  <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                    <div className="rounded-[10px] bg-white overflow-hidden">
                      <button
                        onClick={() => setCoreExpanded(!coreExpanded)}
                        className="w-full px-5 py-4 flex items-center justify-between bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                      >
                        <span className="font-medium text-gray-900">
                          Core Tests ({selectedCoreCategories.length}/{coreCategories.length})
                        </span>
                        <span className="text-sm text-gray-500">
                          {coreExpanded ? "Hide" : "Show"}
                        </span>
                      </button>
                      {coreExpanded && (
                        <div className="px-5 py-4 border-t space-y-2">
                          {coreCategories.map((category) => (
                            <TestCategoryCard
                              key={category.id}
                              id={category.id}
                              name={category.name}
                              description={category.description}
                              isSelected={selectedCoreCategories.includes(category.id)}
                              onToggle={() => handleToggleCoreCategory(category.id)}
                              disabled={isTesting}
                              variant="core"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Recommended Tests Section */}
                  <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                    <div className="rounded-[10px] bg-white overflow-hidden">
                      <div className="px-5 py-4 flex items-center justify-between bg-gray-50/50">
                        <button
                          onClick={() => setSuggestedExpanded(!suggestedExpanded)}
                          className="font-medium text-gray-900"
                        >
                          Recommended ({selectedSuggestedIds.length}/{suggestedTests.length})
                        </button>
                        <div className="flex items-center gap-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              regenerate();
                            }}
                            disabled={isGenerating}
                            className="text-sm text-genie-primary hover:underline disabled:opacity-50"
                          >
                            {isGenerating ? "Generating..." : "Regenerate"}
                          </button>
                          <button
                            onClick={() => setSuggestedExpanded(!suggestedExpanded)}
                            className="text-sm text-gray-500"
                          >
                            {suggestedExpanded ? "Hide" : "Show"}
                          </button>
                        </div>
                      </div>
                      {suggestedExpanded && (
                        <div className="px-5 py-4 border-t space-y-2">
                          {suggestedTests.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-4">
                              No recommendations yet. Click "Regenerate" to create tailored tests.
                            </p>
                          ) : (
                            suggestedTests.map((test) => (
                              <TestCategoryCard
                                key={test.id}
                                id={test.id}
                                name={test.name}
                                description={test.description}
                                isSelected={selectedSuggestedIds.includes(test.id)}
                                onToggle={() => handleToggleSuggested(test.id)}
                                disabled={isTesting}
                                variant="suggested"
                                reason={test.reason}
                              />
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Custom Tests Section */}
                  <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                    <div className="rounded-[10px] bg-white overflow-hidden">
                      <div className="px-5 py-4 flex items-center justify-between bg-purple-50/50">
                        <button
                          onClick={() => setCustomExpanded(!customExpanded)}
                          className="font-medium text-purple-900"
                        >
                          My Custom Tests ({selectedCustomIds.length}/{customTests.length})
                        </button>
                        <div className="flex items-center gap-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCreateCustomTest(true);
                            }}
                            disabled={isTesting}
                            className="text-sm text-purple-600 hover:text-purple-800"
                          >
                            Add Test
                          </button>
                          <button
                            onClick={() => setCustomExpanded(!customExpanded)}
                            className="text-sm text-gray-500"
                          >
                            {customExpanded ? "Hide" : "Show"}
                          </button>
                        </div>
                      </div>
                      {customExpanded && (
                        <div className="px-5 py-4 border-t space-y-2">
                          {customTests.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-4">
                              No custom tests yet. Create tests for scenarios unique to your business.
                            </p>
                          ) : (
                            customTests.map((test) => (
                              <TestCategoryCard
                                key={test.id}
                                id={test.id}
                                name={test.name}
                                description={test.description}
                                isSelected={selectedCustomIds.includes(test.id)}
                                onToggle={() => handleToggleCustom(test.id)}
                                disabled={isTesting}
                                variant="custom"
                              />
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sticky Footer */}
            <div className="sticky bottom-0 border-t px-6 sm:px-8 py-4 flex justify-start gap-3 bg-white">
              <Button variant="outline" onClick={handleClose} disabled={isTesting}>
                Cancel
              </Button>
              <Button
                onClick={handleRunTest}
                disabled={isTesting || isTestRunning || totalSelectedCount === 0 || isOverLimit || !elevenlabsId || isLoadingAny}
                className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
              >
                {isTesting || isTestRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : isLoadingAny ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : !elevenlabsId ? (
                  "Configure Genie First"
                ) : totalSelectedCount === 0 ? (
                  "Select Tests"
                ) : (
                  `Run ${totalSelectedCount} Test${totalSelectedCount !== 1 ? "s" : ""}`
                )}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Create Custom Test Modal */}
      <CreateCustomTestModal
        isOpen={showCreateCustomTest}
        onClose={() => setShowCreateCustomTest(false)}
        onCreated={(newTest) => {
          setSelectedCustomIds((prev) => [...prev, newTest.id]);
        }}
      />
    </>
  );
};
